import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { FontAwesomeSharedModule } from '../../font-awesome/font-awesome';
import { NotificationService } from '../../../views/notifications/services/notification.service';
import { NotificationsFacade } from '../../../views/notifications/facade/notification.facade';
import { NotificationsApiActions } from '../../../views/notifications/state/actions';
import { Notification } from '../../../views/notifications/models/Notifications';
import { NotificationLinkService } from '../../services/notification-link.service';
import { SeenTracker } from '../../utils/seen-tracker';

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, FontAwesomeSharedModule],
  templateUrl: './notification-bell.component.html',
})
export class NotificationBellComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly notificationsFacade = inject(NotificationsFacade);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly notificationLinks = inject(NotificationLinkService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('dd') private dd?: ElementRef<HTMLDetailsElement>;
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('topSentinel') private topSentinel?: ElementRef<HTMLElement>;
  @ViewChild('bottomSentinel') private bottomSentinel?: ElementRef<HTMLElement>;
  @ViewChildren('row') private rows?: QueryList<ElementRef<HTMLElement>>;

  readonly items = signal<Notification[]>([]);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(true);
  readonly unreadCount = signal(0);
  /** Whether each end of the list is in view; a scroll shadow shows on an end that is not. */
  readonly atTop = signal(true);
  readonly atEnd = signal(true);
  /** Loaded IDs that are still unread (for row highlighting). */
  private readonly sessionUnreadIds = signal<Set<number>>(new Set());

  readonly badgeLabel = computed(() => {
    const count = this.unreadCount();
    if (count <= 0) return '';
    return count > 9 ? '9+' : String(count);
  });

  private nextOffset = 0;
  private refreshing = false;
  private pageSub?: Subscription;
  private readonly seen = new SeenTracker((ids) => this.notificationsFacade.markNotificationsRead(ids));
  /** A page requested before a mark was saved can arrive after its confirmation, so confirmed reads win. */
  private readonly confirmedReadIds = new Set<number>();
  private sentinelObserver?: IntersectionObserver;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Marks made on the notifications page land here too, so the badge and highlights stay in step.
    this.actions$
      .pipe(ofType(NotificationsApiActions.MarkNotificationsReadSuccess), takeUntilDestroyed())
      .subscribe(({ ids, count }) => {
        ids.forEach((id) => this.confirmedReadIds.add(id));
        this.unreadCount.set(count);
        this.sessionUnreadIds.update((unread) => new Set([...unread].filter((id) => !ids.includes(id))));
      });
  }

  ngOnInit(): void {
    this.fetchUnreadCount();
    this.refreshFirstPage();
    this.pollTimer = setInterval(() => this.fetchUnreadCount(), POLL_INTERVAL_MS);
  }

  ngAfterViewInit(): void {
    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === this.topSentinel?.nativeElement) {
            this.atTop.set(entry.isIntersecting);
            continue;
          }
          this.atEnd.set(entry.isIntersecting);
          if (entry.isIntersecting) {
            this.loadNextPage();
          }
        }
      },
      { root: this.scrollContainer?.nativeElement },
    );
    for (const sentinel of [this.topSentinel, this.bottomSentinel]) {
      if (sentinel) {
        this.sentinelObserver.observe(sentinel.nativeElement);
      }
    }

    this.rows?.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.observeUnreadRows());
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.pageSub?.unsubscribe();
    this.sentinelObserver?.disconnect();
    this.seen.reset();
  }

  onToggle(): void {
    const open = this.dd?.nativeElement.open ?? false;
    if (open) {
      this.observeUnreadRows();
      this.refreshFirstPage();
    } else {
      this.endSession();
    }
  }

  openPreferences(): void {
    this.close();
    void this.router.navigate(['/notifications/preferences']);
  }

  openAll(): void {
    this.close();
    void this.router.navigate(['/notifications']);
  }

  /**
   * Go straight to what the notification is about when it names a destination.
   * Without one there is nowhere better to land than the notification itself.
   */
  openNotification(notification: Notification): void {
    this.seen.report(notification.id);
    this.seen.flush();
    this.close();
    if (this.notificationLinks.canOpen(notification)) {
      this.notificationLinks.open(notification);
      return;
    }
    void this.router.navigate(['/notifications'], {
      queryParams: { id: notification.id },
    });
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'success':
        return 'badge badge-success badge-sm';
      case 'warning':
        return 'badge badge-warning badge-sm';
      case 'failed':
      case 'error':
        return 'badge badge-error badge-sm';
      case 'info':
        return 'badge badge-info badge-sm';
      default:
        return 'badge badge-neutral badge-sm';
    }
  }

  isUnread(notification: Notification): boolean {
    return this.sessionUnreadIds().has(notification.id);
  }

  private fetchUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (response) => this.unreadCount.set(response.count ?? 0),
      error: () => {
        // leave previous count on failure
      },
    });
  }

  /** Replaces the list with the newest page; until it arrives the panel shows what it already has. */
  private refreshFirstPage(): void {
    this.pageSub?.unsubscribe();
    this.loadingMore.set(false);
    this.refreshing = true;
    this.pageSub = this.notificationService
      .getNotifications({ limit: PAGE_SIZE, offset: 0 }, { silent: true })
      .subscribe({
        next: (response) => {
          const results = response.results ?? [];
          this.refreshing = false;
          this.nextOffset = results.length;
          this.hasMore.set(!!response.next);
          this.items.set(results);
          this.sessionUnreadIds.set(
            new Set(results.filter((n) => this.isUnreadOnArrival(n)).map((n) => n.id)),
          );
          this.rearmBottomSentinel();
        },
        error: () => {
          this.refreshing = false;
          if (this.items().length === 0) {
            this.hasMore.set(false);
          }
        },
      });
  }

  private loadNextPage(): void {
    if (!this.hasMore() || this.refreshing || this.loadingMore()) return;
    this.loadingMore.set(true);
    this.pageSub = this.notificationService
      .getNotifications({ limit: PAGE_SIZE, offset: this.nextOffset }, { silent: true })
      .subscribe({
        next: (response) => {
          const results = response.results ?? [];
          this.nextOffset += results.length;
          this.hasMore.set(!!response.next);
          this.appendPage(results);
          this.loadingMore.set(false);
          this.rearmBottomSentinel();
        },
        error: () => {
          this.loadingMore.set(false);
          this.hasMore.set(false);
        },
      });
  }

  private appendPage(results: Notification[]): void {
    // Notifications created while the panel is open shift the offsets, so a page can repeat loaded rows.
    const loadedIds = new Set(this.items().map((n) => n.id));
    const fresh = results.filter((n) => !loadedIds.has(n.id));
    this.items.update((items) => [...items, ...fresh]);

    const unreadIds = fresh.filter((n) => this.isUnreadOnArrival(n)).map((n) => n.id);
    if (unreadIds.length > 0) {
      this.sessionUnreadIds.update((ids) => new Set([...ids, ...unreadIds]));
    }
  }

  private isUnreadOnArrival(notification: Notification): boolean {
    return (
      notification.is_read === false &&
      !this.confirmedReadIds.has(notification.id) &&
      !this.seen.hasReported(notification.id)
    );
  }

  /** IntersectionObserver reports only changes; re-observing re-checks a sentinel that stayed in view. */
  private rearmBottomSentinel(): void {
    const sentinel = this.bottomSentinel?.nativeElement;
    if (!sentinel || !this.sentinelObserver) return;
    this.sentinelObserver.unobserve(sentinel);
    this.sentinelObserver.observe(sentinel);
  }

  private observeUnreadRows(): void {
    const unreadIds = this.sessionUnreadIds();
    for (const row of this.rows ?? []) {
      if (unreadIds.has(Number(row.nativeElement.dataset['id']))) {
        this.seen.observe(row.nativeElement);
      }
    }
  }

  /** Sends pending marks and keeps the loaded rows, with their read state, for the next open. */
  private endSession(): void {
    this.pageSub?.unsubscribe();
    this.refreshing = false;
    this.loadingMore.set(false);
    this.seen.reset();
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }
  }

  private close(): void {
    if (this.dd?.nativeElement) {
      this.dd.nativeElement.open = false;
    }
  }
}
